import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articles } from "@/mocks/articles";

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://vigorbuy.blog';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <section className="relative w-full pt-36 pb-20 text-center">
          <div className="max-w-xl mx-auto px-6">
            <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-6">
              <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-foreground-950 font-heading mb-3">
              Article Not Found
            </h1>
            <p className="text-foreground-600 mb-6">
              The article you are looking for does not exist or may have been moved.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-semibold rounded-lg text-sm whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              <i className="ri-home-line"></i>
              Back to Homepage
            </Link>
          </div>
        </section>
        <Footer />
      </main>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.description,
    "datePublished": article.date,
    "dateModified": article.date,
    "author": {
      "@type": "Organization",
      "name": "Vigorbuy.blog"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Vigorbuy.blog"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${SITE_URL}/article/${article.slug}`
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": `${SITE_URL}/`
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Shopping Guides",
        "item": `${SITE_URL}/vigorbuy-sheet-guide`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": article.title,
        "item": `${SITE_URL}/article/${article.slug}`
      }
    ]
  };

  return (
    <main className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}></script>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}></script>
      <Navbar />

      {/* Article Header */}
      <section className="relative w-full pt-28 pb-12 md:pt-36 md:pb-16 bg-background-100">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <nav className="mb-6 flex items-center gap-2 text-sm flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium transition-colors">
              Home
            </Link>
            <span className="text-foreground-300">/</span>
            <Link to="/vigorbuy-sheet-guide" className="text-primary-600 hover:text-primary-700 font-medium transition-colors">
              Guides
            </Link>
            <span className="text-foreground-300">/</span>
            <span className="text-foreground-500">{article.category}</span>
          </nav>

          <p className="text-sm font-semibold text-accent-600 uppercase tracking-wider mb-3">
            {article.category}
          </p>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-foreground-950 font-heading leading-tight mb-5 tracking-tight">
            {article.title}
          </h1>
          <p className="text-base text-foreground-600 mb-6">
            {article.description}
          </p>
          <div className="flex items-center gap-4 text-sm text-foreground-400">
            <span>Updated: {article.date}</span>
            <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
            <span>{article.readTime}</span>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <div className="max-w-4xl mx-auto px-6 md:px-10 -mt-6 mb-12">
        <div className="rounded-lg overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            title={article.title}
            className="w-full h-auto object-cover"
          />
        </div>
      </div>

      {/* Article Content */}
      <article className="max-w-3xl mx-auto px-6 md:px-10 pb-16 md:pb-24">
        <div className="prose-custom space-y-10">
          {article.sections.map((section, sIdx) => (
            <section key={sIdx}>
              <h2 className="text-2xl md:text-3xl font-extrabold text-foreground-950 font-heading mb-4">
                {section.heading}
              </h2>

              {section.content.map((paragraph, pIdx) => (
                <p key={pIdx} className="text-base text-foreground-700 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: paragraph }}></p>
              ))}

              {section.listItems && section.listItems.length > 0 && (
                section.listType === "ol" ? (
                  <ol className="list-decimal list-inside space-y-2 text-base text-foreground-700 leading-relaxed mb-4">
                    {section.listItems.map((item, lIdx) => (
                      <li key={lIdx}>{item}</li>
                    ))}
                  </ol>
                ) : (
                  <ul className="space-y-2 mb-4">
                    {section.listItems.map((item, lIdx) => (
                      <li key={lIdx} className="flex gap-3 text-base text-foreground-700">
                        <span className="text-primary-500 flex-shrink-0 mt-1">
                          <i className="ri-checkbox-circle-fill text-sm"></i>
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )
              )}

              {section.tips && section.tips.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {section.tips.map((tip, tIdx) => (
                    <div key={tIdx} className="p-4 rounded-lg bg-background-100 border border-background-200/60">
                      <h4 className="font-bold text-foreground-950 mb-2 text-sm md:text-base">{tip.title}</h4>
                      <p className="text-sm text-foreground-600 leading-relaxed">{tip.desc}</p>
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))}

          {/* Final Internal CTA */}
          <section className="bg-primary-50 rounded-lg p-6 md:p-8 border border-primary-200/50">
            <h2 className="text-xl md:text-2xl font-extrabold text-foreground-950 font-heading mb-3">
              Explore More Vigorbuy Spreadsheet Guides
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-5">
              Ready to discover more? Browse our complete collection of shopping guides, browse the
              14 product categories, and start finding the best products with confidence today.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-semibold rounded-lg text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors duration-200"
              >
                Open Vigorbuy Sheet
                <i className="ri-arrow-right-line"></i>
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-background-50 text-foreground-900 font-semibold rounded-lg text-sm border border-background-200/60 whitespace-nowrap cursor-pointer hover:bg-background-100 transition-colors duration-200"
              >
                <i className="ri-home-line"></i>
                Back to Homepage
              </Link>
            </div>
          </section>
        </div>

        {/* Disclaimer */}
        <div className="mt-12 p-4 rounded-lg bg-secondary-50 border border-secondary-200/50">
          <p className="text-xs text-foreground-500 leading-relaxed">
            <strong>Editorial Disclaimer:</strong> Vigorbuy.blog is an independent shopping guide and
            product discovery platform. We do not sell products directly. All product links direct you
            to third-party shopping sites. Products are researched independently and our recommendations
            are based on quality control reviews and community feedback. Prices and availability are
            subject to change. Last updated: {article.date}.
          </p>
        </div>
      </article>

      <Footer />
    </main>
  );
}